import React, { useState, useEffect } from 'react';
import { X, Mail, Link as LinkIcon, Paperclip, Send, Loader, Briefcase, Building2, Share2, ChevronRight, ChevronLeft } from 'lucide-react';
import { candidatesAPI, clientsAPI, jobsAPI } from "../../services/api";
import { toast } from "react-toastify";

const SHARE_TYPES = {
  LINK: {
    key: 'LINK',
    label: 'Send Portal Link',
    sublabel: 'Client gets a URL to review candidates in their browser',
    icon: LinkIcon,
    color: 'indigo',
    apiMethod: (linkId) => candidatesAPI.sendLinkEmail(linkId),
    successMsg: 'Portal link email sent successfully!',
  },
  ATTACHMENT: {
    key: 'ATTACHMENT',
    label: 'Send Excel Attachment',
    sublabel: 'Client gets an Excel file with all candidate details',
    icon: Paperclip,
    color: 'emerald',
    apiMethod: (linkId) => candidatesAPI.sendAttachmentEmail(linkId),
    successMsg: 'Candidate list attachment sent successfully!',
  },
};

const ShareCandidatesModal = ({ 
  selectedCandidateIds, 
  onClose, 
  initialClient = null, 
  initialJob = null, 
  candidates = [],
  isRecommendFlow = false 
}) => {
  const [shareType, setShareType] = useState('LINK');
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedClient, setSelectedClient] = useState(initialClient);
  const [selectedJob, setSelectedJob] = useState(initialJob);
  const [emailInput, setEmailInput] = useState('');
  const [expiryHours, setExpiryHours] = useState(48);

  // Fetch clients on mount
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await clientsAPI.getAllClients();
        setClients(Array.isArray(res.data) ? res.data : res.data.content || []);
      } catch (err) {
        console.error('Error fetching clients:', err);
      }
    };
    fetchClients();
  }, []);

  // Fetch jobs when client changes
  useEffect(() => {
    const fetchJobs = async () => {
      if (selectedClient) {
        try {
          const res = await jobsAPI.getJobsByClientId(selectedClient.id);
          const data = Array.isArray(res.data) ? res.data : res.data.content || [];
          setJobs(data);
          if (!emailInput) {
            setEmailInput(selectedClient.pocEmail || '');
          }
        } catch (err) {
          console.error('Error fetching jobs:', err);
          setJobs([]);
        }
      }
    };
    fetchJobs();
  }, [selectedClient]);

  const handleConfirm = async () => {
    if (!selectedClient) { toast.error('Please select a client'); return; }
    if (!selectedJob) { toast.error('Please select a job'); return; }
    if (!emailInput || !emailInput.includes('@')) { toast.error('Please enter a valid email address'); return; }
    if (!shareType) { toast.error('Please select a delivery method'); return; }
    
    setLoading(true);
    try {
      if (isRecommendFlow) {
        try {
          await Promise.allSettled(
            selectedCandidateIds.map(candidateId =>
              candidatesAPI.addJobToApplication(candidateId, {
                jobId: selectedJob.id,
                clientId: selectedClient.id,
              })
            )
          );
          toast.success('Candidates associated with new job role');
        } catch (recErr) {
          console.error('Recommendation failed:', recErr);
        }
      }

      const mappedApplicationIds = selectedCandidateIds.map(id => {
        const candidate = candidates.find(c => c.id === id);
        return candidate?.applicationId || id;
      });

      const payload = {
        clientId: selectedClient.id,
        jobId: selectedJob.id,
        clientEmail: emailInput,
        applicationIds: mappedApplicationIds,
        expiryHours: parseInt(expiryHours, 10)
      };

      const createRes = await candidatesAPI.generateShareableLink(payload);
      const linkId = createRes.data?.linkId || createRes.data?.id;

      if (!linkId) {
        toast.error('Link created but ID not returned');
        setLoading(false);
        return;
      }

      await SHARE_TYPES[shareType].apiMethod(linkId);
      toast.success(SHARE_TYPES[shareType].successMsg);

      if (createRes.data?.shareableUrl) {
        navigator.clipboard.writeText(createRes.data.shareableUrl).catch(() => {});
        toast.info('Portal link also copied to clipboard');
      }

      onClose();
    } catch (err) {
      console.error('Error sharing candidates:', err);
      toast.error('Failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200 flex flex-col max-h-[90vh]">
        <div className="px-6 pt-6 pb-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
          <div>
            <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-0.5">
              {isRecommendFlow ? 'Recommend & Share' : 'Share Candidates'}
            </p>
            <h2 className="text-lg font-bold text-slate-900">Confirm Details & Select Method</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="px-6 py-5 space-y-6">
            {/* Summary Section */}
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
              <div className="flex items-center gap-2 text-slate-600 mb-2">
                <Share2 size={14} />
                <span className="text-xs font-semibold uppercase tracking-wider">Sharing Summary</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Selected Candidates</span>
                <span className="text-sm font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">
                  {selectedCandidateIds.length}
                </span>
              </div>
            </div>

            {/* Placement Details */}
            <div className="space-y-4">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <div className="w-1 h-4 bg-indigo-500 rounded-full"></div>
                Placement Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Client Selection */}
                <div>
                  <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5 block">
                    Select Client <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Building2 size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <select
                      value={selectedClient?.id || ''}
                      onChange={(e) => {
                        const client = clients.find(c => c.id === e.target.value);
                        setSelectedClient(client || null);
                        setSelectedJob(null);
                      }}
                      disabled={!!initialClient}
                      className="w-full pl-9 pr-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white disabled:bg-slate-50"
                    >
                      <option value="">— Select a Client —</option>
                      {clients.map(c => (
                        <option key={c.id} value={c.id}>{c.companyName}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Job Selection */}
                <div>
                  <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5 block">
                    Select Job <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Briefcase size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <select
                      value={selectedJob?.id || ''}
                      onChange={(e) => {
                        const job = jobs.find(j => j.id === e.target.value);
                        setSelectedJob(job || null);
                      }}
                      disabled={!!initialJob || !selectedClient || jobs.length === 0}
                      className="w-full pl-9 pr-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white disabled:bg-slate-50"
                    >
                      <option value="">
                        {!selectedClient ? 'Select a client first' : jobs.length === 0 ? 'No jobs found' : '— Select a Job —'}
                      </option>
                      {jobs.map(j => (
                        <option key={j.id} value={j.id}>{j.jobCode} — {j.jobTitle}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Recipient Details */}
            <div className="space-y-4">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <div className="w-1 h-4 bg-indigo-500 rounded-full"></div>
                Recipient Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5 block">
                    Recipient Email <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="email"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      placeholder="client@example.com"
                      className="w-full pl-9 pr-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5 block">
                    Link Expiry (hours)
                  </label>
                  <input
                    type="number"
                    value={expiryHours}
                    onChange={(e) => setExpiryHours(e.target.value)}
                    min="1"
                    max="168"
                    className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
                  />
                </div>
              </div>
            </div>

            {/* Delivery Method */}
            <div className="space-y-4">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <div className="w-1 h-4 bg-indigo-500 rounded-full"></div>
                Delivery Method
              </h3>
              <div className="grid grid-cols-1 gap-3">
                {Object.values(SHARE_TYPES).map((type) => {
                  const Icon = type.icon;
                  const isActive = shareType === type.key;
                  return (
                    <button
                      key={type.key}
                      onClick={() => setShareType(type.key)}
                      className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
                        isActive 
                          ? (type.color === 'indigo' ? 'border-indigo-600 bg-indigo-50/50' : 'border-emerald-600 bg-emerald-50/50')
                          : 'border-slate-100 hover:border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        isActive 
                          ? (type.color === 'indigo' ? 'bg-indigo-600 text-white' : 'bg-emerald-600 text-white')
                          : (type.color === 'indigo' ? 'bg-indigo-100 text-indigo-600' : 'bg-emerald-100 text-emerald-600')
                      }`}>
                        <Icon size={22} />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-slate-900 text-sm">{type.label}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{type.sublabel}</div>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        isActive 
                          ? (type.color === 'indigo' ? 'border-indigo-600' : 'border-emerald-600')
                          : 'border-slate-200'
                      }`}>
                        {isActive && <div className={`w-2.5 h-2.5 rounded-full ${type.color === 'indigo' ? 'bg-indigo-600' : 'bg-emerald-600'}`}></div>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-6 border-t border-slate-100 bg-slate-50 flex-shrink-0">
          <button
            onClick={handleConfirm}
            disabled={loading || !selectedJob || !emailInput || !shareType}
            className="w-full px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-base font-bold flex items-center justify-center gap-3 disabled:opacity-60 transition-all shadow-lg shadow-indigo-100 active:scale-[0.98]"
          >
            {loading ? (
              <><Loader size={20} className="animate-spin" /> Sending...</>
            ) : (
              <><Send size={20} /> Confirm & Send sharing</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareCandidatesModal;
